import { Component, OnInit, Renderer2, ElementRef, inject } from '@angular/core';

@Component({
    selector: 'app-dummy-site',
    templateUrl: './dummy-site.component.html',
    styleUrls: ['./dummy-site.component.css'],
    standalone: false
})
export class DummySiteComponent implements OnInit {
    private renderer = inject(Renderer2);
    private el = inject(ElementRef);

    isAccordionOpen = false;
    private activeModal: HTMLElement | null = null;
    private closeOnBackdropClick: boolean = true;

    ngOnInit(): void {
        this.setupAccordions();
        this.setupModalTriggers();
    }


    private setupModalTriggers(): void {
        this.el.nativeElement.querySelectorAll('[data-oc-modal]').forEach((button: HTMLElement) => {
            this.renderer.listen(button, 'click', () => {
                const modalId = button.getAttribute('data-oc-modal');
                const closeOnBackdrop = button.getAttribute('data-oc-close-on-backdrop-click');
                this.openModal(modalId, closeOnBackdrop !== 'false');
            });
        });
    }

    private openModal(modalId: string | null, closeOnBackdropClick: boolean): void {
        if (!modalId) return;

        this.activeModal = this.el.nativeElement.querySelector(modalId);
        if (!this.activeModal) return;

        this.closeOnBackdropClick = closeOnBackdropClick;
        this.renderer.addClass(this.activeModal, 'show');
        this.ensureBackdrop();
        this.setupCloseInteractions();
    }

    private ensureBackdrop(): void {
        if (!this.activeModal) return;

        let backdrop = this.activeModal.querySelector('.modal-backdrop');
        if (!backdrop) {
            backdrop = this.renderer.createElement('div');
            this.renderer.addClass(backdrop, 'modal-backdrop');
            this.renderer.appendChild(this.activeModal, backdrop);
        }
        this.renderer.addClass(backdrop, 'show');
    }

    private setupCloseInteractions(): void {
        if (!this.activeModal) return;

        const closeButtons = this.activeModal.querySelectorAll('[data-oc-dismiss="modal"], .btn-close');
        const backdrop = this.activeModal.querySelector('.modal-backdrop');

        closeButtons.forEach(button =>
            this.renderer.listen(button, 'click', () => this.closeModal()),
        );

        if (backdrop && this.closeOnBackdropClick) {
            this.renderer.listen(backdrop, 'click', (event: Event) => {
                if (event.target === backdrop) this.closeModal();
            });
        }
    }

    private closeModal(): void {
        if (!this.activeModal) return;

        this.renderer.removeClass(this.activeModal, 'show');
        const backdrop = this.activeModal.querySelector('.modal-backdrop');
        if (backdrop) {
            this.renderer.removeClass(backdrop, 'show');
        }
        this.activeModal = null;
    }


    private readonly ANIMATION_DURATION = 350;


    private setupAccordions(): void {
        const accordionButtons = this.el.nativeElement.querySelectorAll('.accordion-button');
        accordionButtons.forEach((button: Element) => {
            if (button instanceof HTMLElement) {
                this.renderer.listen(button, 'click', (event: Event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.toggleAccordion(button);
                });
            }
        });
    }

    private toggleAccordion(button: HTMLElement): void {
        const accordionItem = button.closest('[data-oc-accordion]');
        if (!(accordionItem instanceof HTMLElement)) return;

        const collapse = accordionItem.querySelector('.accordion-collapse');
        if (!(collapse instanceof HTMLElement)) return;

        const isOpen = collapse.classList.contains('open');

        // Schließe alle anderen offenen Akkordeons auf der gleichen Ebene
        const parentAccordion = accordionItem.closest('.accordion');
        if (parentAccordion instanceof HTMLElement) {
            const siblingCollapses = parentAccordion.querySelectorAll('.accordion-collapse.open');
            siblingCollapses.forEach((sibling: Element) => {
                if (sibling instanceof HTMLElement && sibling !== collapse) {
                    this.renderer.removeClass(sibling, 'open');
                    this.setCollapseMaxHeight(sibling);
                }
            });
        }

        // Toggle die 'open' Klasse für das aktuelle Akkordeon
        if (isOpen) {
            this.renderer.removeClass(collapse, 'open');
        } else {
            this.renderer.addClass(collapse, 'open');
        }

        this.setCollapseMaxHeight(collapse);

        // Entferne max-height von allen übergeordneten .accordion-collapse Elementen
        this.removeParentMaxHeights(collapse);

        // Setze die korrekte max-height für übergeordnete Akkordeons nach Abschluss der Animation
        setTimeout(() => {
            this.updateParentMaxHeights(collapse);
        }, this.ANIMATION_DURATION);
    }

    private setCollapseMaxHeight(collapse: HTMLElement): void {
        if (collapse.classList.contains('open')) {
            collapse.style.maxHeight = collapse.scrollHeight + 'px';
        } else {
            collapse.style.maxHeight = '0';
        }
    }

    private removeParentMaxHeights(element: HTMLElement): void {
        let parent = element.parentElement;
        while (parent) {
            const parentCollapse = parent.closest('.accordion-collapse');
            if (parentCollapse instanceof HTMLElement) {
                parentCollapse.style.maxHeight = 'none';
                parent = parentCollapse.parentElement;
            } else {
                break;
            }
        }
    }

    private updateParentMaxHeights(element: HTMLElement): void {
        let parent = element.parentElement;
        while (parent) {
            const parentCollapse = parent.closest('.accordion-collapse');
            if (parentCollapse instanceof HTMLElement) {
                this.setCollapseMaxHeight(parentCollapse);
                parent = parentCollapse.parentElement;
            } else {
                break;
            }
        }
    }
}
